/**
 * Agent Auth Callback
 * Handles the Slack OAuth callback for agent authentication
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Avatar
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

const AgentAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing authentication...');
  const [agentInfo, setAgentInfo] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Slack authentication failed: ${error}`);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setMessage('Missing authentication parameters. Please try again.');
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/call-centre/auth/slack/callback`, {
          code,
          state
        });

        if (response.data.success) {
          // Store token and agent info
          localStorage.setItem('agentToken', response.data.token);
          localStorage.setItem('agentInfo', JSON.stringify(response.data.agent));

          setAgentInfo(response.data.agent);
          setStatus('success');
          setMessage('Successfully authenticated!');

          // Redirect after a short delay to show success
          setTimeout(() => {
            navigate(response.data.redirect || '/call-centre');
          }, 1500);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Authentication failed');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage(err.response?.data?.message || 'Failed to complete authentication');

        // If the error includes Slack user ID, show it for debugging
        if (err.response?.data?.slackUserId) {
          setMessage(prev =>
            `${prev}\n\nYour Slack User ID: ${err.response.data.slackUserId}\n` +
            `Please ask your administrator to add you as an agent.`
          );
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #4F46E5 0%, var(--color-primary) 50%, var(--color-accent) 100%)',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3
          }}
        >
          {/* Processing State */}
          {status === 'processing' && (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Authenticating...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {message}
              </Typography>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                Welcome!
              </Typography>
              {agentInfo && (
                <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  {agentInfo.picture && (
                    <Avatar src={agentInfo.picture} sx={{ width: 56, height: 56 }} />
                  )}
                  <Typography variant="h6">
                    {agentInfo.name || agentInfo.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agentInfo.brands?.length} brand{agentInfo.brands?.length !== 1 ? 's' : ''} assigned
                  </Typography>
                </Stack>
              )}
              <Typography variant="body2" color="text.secondary">
                Redirecting to dashboard...
              </Typography>
              <CircularProgress size={20} sx={{ mt: 2 }} />
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                Authentication Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/call-centre/login')}
              >
                Try Again
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AgentAuthCallback;
